<template>
  <AppNavbar />
  <div class="vx-admin">
    <div style="max-width: 1100px">

      <div class="mb-3">
        <div class="vx-migas mb-1">
          <span>Cobranza</span><span class="sep">/</span>
          <router-link :to="{ name: 'Suscripciones' }">Suscripciones</router-link>
          <span class="sep">/</span><span>Inscribir varios</span>
        </div>
        <h1 class="vx-titulo">Inscribir varios</h1>
        <div class="vx-ayuda mt-1">
          Para dar de alta una sección entera de una vez. Es el mismo alta de siempre,
          repetido: cada fila necesita su ficha y su responsable de pago.
        </div>
      </div>

      <!-- 01 — A qué esquema -->
      <div class="vx-panel mb-3">
        <div class="vx-panel-cabecera">
          <div class="d-flex align-items-baseline gap-2">
            <span class="vx-paso">01</span>
            <h2 class="vx-panel-titulo">A qué esquema entran</h2>
          </div>
        </div>
        <div class="p-3">
          <div class="row g-3">
            <div class="col-md-5">
              <label class="vx-etiqueta" for="l-esquema">Esquema</label>
              <select id="l-esquema" v-model="form.cr_esquema_id" class="vx-campo">
                <option :value="null" disabled>Elegí un esquema…</option>
                <option v-for="e in esquemas" :key="e.cr_esquema_id" :value="e.cr_esquema_id">
                  {{ e.nombre }}
                </option>
              </select>
            </div>
            <div class="col-md-2">
              <label class="vx-etiqueta" for="l-vigencia">Vigencia desde</label>
              <input id="l-vigencia" v-model="form.vigencia_desde" class="vx-campo" type="date">
            </div>
            <!-- CR-E7-002 / RN-53: toda la sección comparte la misma ventana.
                 Es lo que la hace una sección. -->
            <div class="col-md-2">
              <label class="vx-etiqueta" for="l-vigencia-hasta">Hasta</label>
              <input id="l-vigencia-hasta" v-model="form.vigencia_hasta" class="vx-campo"
                     type="date" :min="form.vigencia_desde">
            </div>
            <div class="col-md-4">
              <label class="vx-etiqueta" for="l-tipo">Qué son</label>
              <input id="l-tipo" v-model.trim="form.ficha_tipo" class="vx-campo"
                     placeholder="alumno" list="tipos-ficha-lote">
              <datalist id="tipos-ficha-lote">
                <option value="alumno"></option><option value="socio"></option>
                <option value="paciente"></option><option value="servicio"></option>
              </datalist>
            </div>
          </div>
          <div class="vx-ayuda mt-2">
            La vigencia y el tipo son los mismos para todas las filas. Lo que cambia
            fila a fila es la persona.
          </div>
        </div>
      </div>

      <!-- 02 — Quiénes -->
      <div class="vx-panel mb-3">
        <div class="vx-panel-cabecera">
          <div class="d-flex align-items-baseline gap-2">
            <span class="vx-paso">02</span>
            <h2 class="vx-panel-titulo">Quiénes</h2>
          </div>
          <span class="vx-ayuda">{{ listas }} de {{ filas.length }} listas</span>
        </div>

        <div style="overflow-x: auto">
          <table class="vx-tabla" data-test="tabla-lote">
            <thead>
              <tr>
                <th style="min-width: 200px">Nombre</th>
                <th style="min-width: 130px">Identificador</th>
                <th style="min-width: 110px">Grado</th>
                <th style="min-width: 260px">Responsable de pago</th>
                <th style="width: 44px"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(fila, i) in filas" :key="i">
                <td>
                  <input
                    v-model.trim="fila.nombre" class="vx-campo vx-campo-sm"
                    placeholder="Ana López"
                    :class="{ 'vx-campo-error': tocado && !fila.nombre && filaUsada(fila) }"
                  >
                </td>
                <td>
                  <input
                    v-model.trim="fila.id" class="vx-campo vx-campo-sm vx-num"
                    placeholder="Cédula o código"
                    :class="{ 'vx-campo-error': tocado && !fila.id && filaUsada(fila) }"
                  >
                </td>
                <td>
                  <input v-model.trim="fila.grado" class="vx-campo vx-campo-sm" placeholder="1ro">
                </td>
                <td>
                  <!-- CR-E6-003. El responsable se busca entre las personas que
                       ya existen y, si no está, se lo crea sin salir de acá:
                       inscribiendo una sección entera es donde más caro sale
                       perder la carga de N filas por un padre que falta. -->
                  <div v-if="fila.responsable" class="d-flex align-items-center gap-2">
                    <span class="vx-estado vx-estado-activa">{{ fila.responsable.nombre_completo }}</span>
                    <button class="vx-btn vx-btn-sm vx-btn-texto py-0" @click="limpiarResponsable(fila)">
                      Cambiar
                    </button>
                  </div>
                  <div v-else class="position-relative">
                    <input
                      v-model.trim="fila.busqueda" class="vx-campo vx-campo-sm"
                      placeholder="Buscar responsable…"
                      :class="{ 'vx-campo-error': tocado && filaUsada(fila) }"
                      @input="buscar(fila)"
                    >
                    <ul
                      v-if="fila.resultados.length"
                      class="list-unstyled position-absolute w-100 mb-0 vx-panel"
                      style="z-index: 20; box-shadow: var(--vx-sombra); max-height: 200px; overflow-y: auto"
                    >
                      <li
                        v-for="c in fila.resultados" :key="c.cliente_id"
                        class="px-2 py-1 border-bottom" style="cursor: pointer; font-size: 12.5px"
                        @click="elegirResponsable(fila, c)"
                      >
                        {{ c.nombre_completo }}
                      </li>
                    </ul>
                    <button
                      type="button" class="vx-btn vx-btn-sm vx-btn-texto py-0 mt-1"
                      data-test="btn-crear-persona"
                      @click="abrirAltaPersona(fila)"
                    >
                      <i class="bi bi-person-plus"></i> Crear
                    </button>
                  </div>
                </td>
                <td class="text-end">
                  <button
                    v-if="filas.length > 1" class="vx-btn vx-btn-sm"
                    title="Quitar fila" @click="filas.splice(i, 1)"
                  >
                    <i class="bi bi-trash"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="p-3 d-flex gap-2 align-items-center">
          <button class="vx-btn vx-btn-sm vx-btn-texto" @click="agregarFila">
            <i class="bi bi-plus-lg"></i> Agregar fila
          </button>
          <button class="vx-btn vx-btn-sm vx-btn-texto" @click="agregarFilas(5)">
            + 5 filas
          </button>
        </div>
      </div>

      <div v-if="error" class="alert alert-danger py-2 px-3 mb-3" style="font-size: 13.5px"
           role="alert" data-test="error-lote">
        {{ error }}
      </div>

      <div class="d-flex justify-content-end gap-2">
        <button class="vx-btn" @click="$router.push({ name: 'Suscripciones' })">
          Cancelar
        </button>
        <button
          class="vx-btn vx-btn-primario" data-test="btn-inscribir"
          :disabled="guardando" @click="inscribir"
        >
          <span v-if="guardando" class="spinner-border spinner-border-sm"></span>
          {{ guardando ? 'Inscribiendo…' : `Inscribir ${listas || ''}`.trim() }}
        </button>
      </div>
      <div v-if="motivoBloqueo" class="vx-ayuda text-end mt-2" :class="{ 'vx-error': tocado }">
        {{ motivoBloqueo }}
      </div>
    </div>

    <RegistrarClienteModal
      :open="Boolean(filaEnAlta)"
      @saved="onPersonaCreada"
      @close="filaEnAlta = null"
    />
  </div>
</template>

<script setup>
// Inscripción de varios suscriptores en una operación.
//
// Hallazgo de la prueba manual del 2026-09-01: inscribir una sección de 25
// alumnos de a uno son 25 pasadas por el formulario de alta.
//
// NO agrega entidades nuevas al modelo —nada de "grupos", que D16 deja fuera
// de v0.1—: es el alta que ya existe, repetida en una transacción.

import { ref, reactive, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import AppNavbar from '@/components/AppNavbar.vue'
import { useNotificationStore } from '@/stores/notifications'
import {
  listarEsquemas, buscarClientes, crearSuscripcionesLote, formatearMonto
} from '@/services/cobranzaService'
import RegistrarClienteModal from '@/components/RegistrarClienteModal.vue'
import { fechaIsoLocal } from '@/utils/fechaLocal.mjs'

const router = useRouter()
const notificaciones = useNotificationStore()

const esquemas = ref([])
const guardando = ref(false)
const error = ref('')
const tocado = ref(false)

const form = reactive({
  cr_esquema_id: null,
  vigencia_desde: fechaIsoLocal(),
  vigencia_hasta: '',
  ficha_tipo: 'alumno'
})

const filaVacia = () => ({ nombre: '', id: '', grado: '', busqueda: '', resultados: [], responsable: null })
const filas = ref([filaVacia(), filaVacia(), filaVacia()])

// Una fila en blanco es la comodidad de tener filas de más: no es un error,
// se descarta al enviar. Una fila a medio completar sí lo es.
const filaUsada = (f) => Boolean(f.nombre || f.id || f.responsable || f.busqueda)
const filaCompleta = (f) => Boolean(f.nombre && f.id && f.responsable)

const usadas = computed(() => filas.value.filter(filaUsada))
const completas = computed(() => filas.value.filter(filaCompleta))
const listas = computed(() => completas.value.length)

const motivoBloqueo = computed(() => {
  if (!form.cr_esquema_id) return 'Elegí a qué esquema entran.'
  if (!form.ficha_tipo) return 'Indicá qué son (alumno, socio, servicio…).'
  if (!usadas.value.length) return 'Cargá al menos una fila.'
  const incompletas = usadas.value.length - completas.value.length
  if (incompletas > 0) {
    return `${incompletas} ${incompletas === 1 ? 'fila está incompleta' : 'filas están incompletas'}: cada una necesita nombre, identificador y responsable.`
  }
  return ''
})

const agregarFila = () => filas.value.push(filaVacia())
const agregarFilas = (n) => { for (let i = 0; i < n; i += 1) agregarFila() }

let temporizador = null
function buscar (fila) {
  clearTimeout(temporizador)
  if (fila.busqueda.length < 2) { fila.resultados = []; return }
  temporizador = setTimeout(async () => {
    try {
      fila.resultados = await buscarClientes(fila.busqueda)
    } catch {
      fila.resultados = []
    }
  }, 250)
}

function elegirResponsable (fila, cliente) {
  fila.responsable = cliente
  fila.resultados = []
  fila.busqueda = ''
}

// --- Alta de una persona que todavía no existe (CR-E6-003, CF18) ----------
//
// Se guarda la FILA, no un booleano: el modal es uno solo para toda la tabla,
// así que al volver hay que saber en cuál de las N filas dejar al responsable
// recién creado. Con un booleano, el cliente aterrizaría siempre en la
// primera — un fallo silencioso, porque la pantalla igual muestra un nombre.
const filaEnAlta = ref(null)

function abrirAltaPersona (fila) {
  fila.resultados = []
  filaEnAlta.value = fila
}

function onPersonaCreada (cliente) {
  const fila = filaEnAlta.value
  filaEnAlta.value = null
  if (fila && cliente?.cliente_id) elegirResponsable(fila, cliente)
}

function limpiarResponsable (fila) {
  fila.responsable = null
  fila.busqueda = ''
}

async function inscribir () {
  tocado.value = true
  error.value = ''
  if (motivoBloqueo.value) return

  guardando.value = true
  try {
    const r = await crearSuscripcionesLote({
      cr_esquema_id: form.cr_esquema_id,
      vigencia_desde: form.vigencia_desde,
      vigencia_hasta: form.vigencia_hasta || null,
      ficha_tipo: form.ficha_tipo,
      filas: completas.value.map((f) => ({
        nombre: f.nombre,
        id: f.id,
        detalle: f.grado ? { grado: f.grado } : {},
        cliente_id: f.responsable.cliente_id
      }))
    })
    notificaciones.notify({
      type: 'success',
      // Se dice cuántas CUOTAS, no sólo cuántas inscripciones (D28):
      // inscribir una sección entera crea cientos de deudas, y el operador
      // tiene que ver el volumen de lo que acaba de hacer.
      message: `${r.cantidad} ${r.cantidad === 1 ? 'inscripción creada' : 'inscripciones creadas'}`
        + ` · ${r.cuotas} cuotas por ${formatearMonto(r.monto_total)}.`
    })
    router.push({ name: 'Suscripciones' })
  } catch (err) {
    error.value = err?.userMessage || 'No se pudo inscribir.'
  } finally {
    guardando.value = false
  }
}

onMounted(async () => {
  try {
    esquemas.value = await listarEsquemas()
    if (esquemas.value.length === 1) form.cr_esquema_id = esquemas.value[0].cr_esquema_id
  } catch (err) {
    error.value = err?.userMessage || 'No se pudieron cargar los esquemas.'
  }
})
</script>
